import type { ComponentProps, FormEvent } from 'react';
import React, { useState } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { BorderColorProps, BorderTopProps, FlexProps } from 'styled-system';
import { borderColor, borderTop, space, width } from 'styled-system';

import type { BaseComponentProps } from '../../../home-club-section/types';
import { MessageType } from '../../enums/Alert';
import OverlayLoader from '../../OverlayLoader';
import { useLocalization } from '../../providers/LocaleProvider';
import { boxSizing } from '../../theme/system-utilities';
import { HomeClubSectionEventName, HomeClubSectionEventPlacement } from '../../tracking/home-club';
import BaseAlert from '../Alert';
import { Flex } from '../Flex';
import { HomeClubBanner } from '../HomeClubBanner';
import HomeClubForm from '../HomeClubForm';
import { HomeClubLegalText } from '../HomeClubLegalText';
import Modal from '../Modal';
import { BodyRegularBR1, HeadlineH5 } from '../Typography';
import { MembershipDiscountHighlight } from './MembershipDiscountHighlight';
import { MembershipDiscountSkipButton } from './MembershipDiscountSkipButton';

interface HomeClubDiscountProps {
  amount: number;
  onSkip: () => void;
}

interface SubscriptionModalProps extends Pick<BaseComponentProps, 'trackingProps'> {
  isLoading: boolean;
  buttonLabel: string;
  isModalOpen: boolean;
  errorText: Nullable<string>;
  handleModalClose?: () => void;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
  homeClubDiscount?: HomeClubDiscountProps;
  withDefaultHeader?: boolean;
}

const StyledContainer = styled(Flex)<FlexProps & BorderTopProps & BorderColorProps>`
  ${borderTop};
  ${borderColor};
`;

const Container = (props: ComponentProps<typeof StyledContainer>) => (
  <StyledContainer
    pt={['sp_16', null, null, 'sp_20']}
    pb="sp_20"
    px={['sp_16', null, null, 'sp_24']}
    flexDirection="column"
    alignItems="center"
    {...props}
  />
);

const UnstyledAlert = styled(BaseAlert)`
  ${space};
  ${width};
  ${boxSizing};
`;

const Alert = (props: ComponentProps<typeof UnstyledAlert>) => (
  <UnstyledAlert width={1} mb="sp_16" boxSizing="border-box" {...props} />
);

const HomeClubSubscriptionModal = ({
  errorText,
  isLoading,
  buttonLabel,
  isModalOpen,
  trackingProps,
  handleSubmit,
  handleModalClose,
  homeClubDiscount,
  withDefaultHeader = true,
}: SubscriptionModalProps) => {
  const { t } = useLocalization();
  const [isSkippingHomeClubDiscount, setIsSkippingHomeClubDiscount] = useState(false);

  const eventName = trackingProps?.eventName ?? HomeClubSectionEventName.Click;

  const onClickContinueWithoutDiscount = () => {
    setIsSkippingHomeClubDiscount(true);
    homeClubDiscount?.onSkip();
  };

  return (
    <Modal
      onClose={handleModalClose ?? (() => undefined)}
      isOpen={isModalOpen}
      headerTitle={t('h24_hc_modal_title')}
      autoFocus={true}
      eventName={eventName}
      withDefaultHeader={withDefaultHeader}
    >
      {!withDefaultHeader && (
        <HeadlineH5 as="h2" textAlign="center" my={['sp_16', 'sp_12', 'sp_16']} px="sp_48">
          {t('h24_hc_modal_title')}
        </HeadlineH5>
      )}
      <Container
        {...(!withDefaultHeader
          ? { borderTop: '1px solid', borderColor: 'sys.neutral.border.weak' }
          : {})}
      >
        {isSkippingHomeClubDiscount && <OverlayLoader borderRadius="medium" />}
        {errorText && <Alert type={MessageType.Error}>{errorText}</Alert>}
        <HomeClubBanner />
        <BodyRegularBR1
          as="p"
          m="sp_0"
          dangerouslySetInnerHTML={{
            __html: t('h24_hc_modal_description'),
          }}
        />
        {homeClubDiscount && <MembershipDiscountHighlight amount={homeClubDiscount.amount} />}
        <HomeClubLegalText />
        <HomeClubForm
          isLoading={isLoading}
          onSubmit={handleSubmit}
          buttonLabel={buttonLabel}
          buttonWidth={1}
          eventName={eventName}
          placement={HomeClubSectionEventPlacement.Modal}
          {...trackingProps}
        />
        {homeClubDiscount && (
          <MembershipDiscountSkipButton
            onSkip={onClickContinueWithoutDiscount}
            eventName={eventName}
          />
        )}
      </Container>
    </Modal>
  );
};

export default HomeClubSubscriptionModal;
